import * as React from "react";
import classNames from "classnames";
import { merge, $ } from "glamor";
import { theme } from "styles/theme";
import { DimensionTab, DimentionActionCommands } from "../../dimension-types";
import Checkbox from "material-ui/Checkbox";
import { ChevronRight, DragHandle } from "@material-ui/icons";
import { DirectionalHint, IconButton, IContextualMenuProps, Label, nuOrderTheme } from "@nuorder/components";
import { DATATEST } from "constants/integration";
import { Card as BaseCard, ListItem, ListItemIcon, Chip } from "@material-ui/core";
import Tooltip from "components/tooltip";
import { SelectedElementsMessage } from "../selected-elements-label";
import { formatMessage } from "lib/globalize-util";

const styles = merge(
    {
        opacity: "1",
        margin: "5px 10px",
    },
    $(" .gutter", {
        paddingLeft: "8px",
        paddingRight: "8px",
    }),
    $(" .dimensionItemContainer", {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    }),
    $(" .dimensionItemContainer.border", {
        borderBottom: `1px solid ${theme.palette.borderColor}`,
    }),
    $(" .dimensionsLabel", {
        display: "flex",
        width: "100%",
        marginTop: "6px",
        padding: "5px 6px",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer !important",
    }),
    $(" .dimensionHeader", {
        display: "flex",
        justifyContent: "space-between",
    }),
    $(" .dragHandler", {
        marginTop: "1px",
    }),
    $(" .dimensionTextContainer", {
        display: "flex",
        flexDirection: "row",
    }),
    $(" .dimensionSelector", {
        marginLeft: "6px",
        width: "100% !important",
        overflow: "hidden !important",
        display: "flex !important",
    }),
    $(" .dimensionSelector label", {
        width: "100% !important",
        zIndex: "10",
        cursor: "pointer",
    }),
    $(" .dimensionSelector .nameLabel", {
        maxWidth: "145px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        zIndex: "10",
        cursor: "pointer",
    }),
    $(" .dimensionSelector .subLabel", {
        display: "flex",
        flexDirection: "row",
        maxWidth: "100%",
    }),
    $(" .dimensionSelector .collectionDimensionCard", {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    }),
    $(" .dimensionSelector .containerNameLabel", {
        display: "flex",
        flexDirection: "row",
        maxWidth: "100%",
        justifyContent: "space-between",
    }),
    $(" .dimensionSelector span", {
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        maxWidth: "100%",
    }),
    $(" .dimensionSelector .subLabel .small", {
        maxWidth: "145px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: "400",
        fontSize: "10px",
    }),
    $(" .dimensionName", {
        marginLeft: "6px",
    }),
    $(" .attributeKaboButton > span", {
        alignItems: "flex-start !important",
    }),
    $(" .dimensionChip", {
        backgroundColor: theme.palette.blue,
        color: theme.palette.white,
        borderRadius: "5px",
        height: "20px",
        fontWeight: "700",
        marginLeft: "10px",
    }),
    $(" .dimensionChip > span", {
        padding: "5px !important",
    }),
    $(" .dimensionSelectorContainer", {
        display: "flex",
        width: "100%",
    })
);

type CardProps = {
    label: string;
    sublabel?: string;
    id: string;
    selected: boolean;
    selectDimensionHandler: () => void;
    onToggleSelect: () => void;
    onDeleteAttributeHandler: () => void;
    onEditAttributeHandler: () => void;
    isDragging: boolean;
    isRollupMode: boolean;
    isUserDefined: boolean;
    optionsCount: number;
    selectedOptionsCount: number;
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
    (
        {
            label,
            sublabel,
            id,
            selected,
            selectDimensionHandler,
            onToggleSelect,
            onDeleteAttributeHandler,
            onEditAttributeHandler,
            isDragging,
            isRollupMode,
            isUserDefined,
            optionsCount,
            selectedOptionsCount,
        },
        ref
    ) => {
        const nameTextRef = React.useRef(null);
        // const subLabelTextRef = React.useRef(null);
        const [isNameTextOverflown, setIsNameTextOverflown] = React.useState(false);
        // const [isSubLabelOverflown, setIsSubLabelOverflown] = React.useState(false);
        const displayDimensionsLabel = selected;
        const getLabel = (cb): React.ReactNode => {
            return (
                <div onClick={cb} className="collectionDimensionCard">
                    <div className="containerNameLabel">
                        <Tooltip
                            title={label}
                            placement="bottom"
                            data-pullout={true}
                            data-own-align={true}
                            disableHoverListener={!isNameTextOverflown}
                            disableFocusListener
                        >
                            <div className="nameLabel" onClick={cb} ref={nameTextRef}>
                                {label}
                            </div>
                        </Tooltip>
                    </div>
                    {sublabel && (
                        <div className="subLabel">
                            <Tooltip title={sublabel} placement="bottom" disableFocusListener>
                                <span className="small">{sublabel}</span>
                            </Tooltip>
                        </div>
                    )}
                </div>
            );
        };

        const kabobProps: IContextualMenuProps = {
            directionalHint: DirectionalHint.leftCenter,
            gapSpace: 0,
            beakWidth: 20,
            directionalHintFixed: false,
            items: [
                {
                    key: DimentionActionCommands.RenameDoorGrouping,
                    text: formatMessage("Rename door grouping"),
                    onClick: onEditAttributeHandler,
                },

                {
                    key: DimentionActionCommands.DeleteDoorGrouping,
                    text: formatMessage("Delete door grouping"),
                    "data-test": DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_DELETE_ATTRIBUTE_DIALOG,
                    onClick: onDeleteAttributeHandler,
                },
            ],
        };
        return (
            <BaseCard
                className="dimensionsItem"
                {...styles}
                ref={ref}
                style={{
                    opacity: isDragging ? 0 : 1,
                }}
            >
                <ListItem
                    classes={{
                        gutters: "gutter",
                    }}
                    data-dimension={label}
                >
                    <div
                        className={classNames("dimensionItemContainer", {
                            border: selected,
                        })}
                        data-test={`${DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.CONTAINER}-${id}`}
                    >
                        <DimensionHeader>
                            <div className="dimensionSelectorContainer">
                                <ListItemIcon ref={ref} className="dragHandler">
                                    <DragHandle />
                                </ListItemIcon>
                                <Checkbox
                                    onCheck={onToggleSelect}
                                    name={DimensionTab.Attributes.toString()}
                                    className="dimensionSelector"
                                    label={getLabel(() => {})}
                                    value={id}
                                    checked={selected}
                                    data-test={`${DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.NAME}-${id}`}
                                    labelStyle={{
                                        marginBottom: 0,
                                        color: nuOrderTheme.palette.greyDarker,
                                    }}
                                />
                            </div>
                            {isUserDefined && <Chip label={formatMessage("User")} className="dimensionChip" />}
                            {isUserDefined && !isRollupMode && (
                                <IconButton
                                    role="button"
                                    className="attributeKaboButton"
                                    aria-haspopup
                                    menuIconProps={{
                                        iconName: "horizKabob24",
                                    }}
                                    data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_ATTRIBUTE_OPTIONS_ITEM}
                                    menuProps={kabobProps}
                                />
                            )}
                        </DimensionHeader>

                        {displayDimensionsLabel && (
                            <Label
                                className="dimensionsLabel"
                                style={{ fontWeight: "inherit" }}
                                onClick={selectDimensionHandler}
                            >
                                <SelectedElementsMessage
                                    size={optionsCount}
                                    selectedOptionsSize={selectedOptionsCount}
                                />
                                <ChevronRight />
                            </Label>
                        )}
                    </div>
                </ListItem>
            </BaseCard>
        );
    }
);

Card.displayName = "AttributeCard";
const DimensionHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="dimensionHeader">{children}</div>
);
